import {
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  BarcodeOutlined,
  BookOutlined,
  BuildOutlined,
  DollarOutlined,
  ContainerOutlined,
  InboxOutlined,
  ShoppingCartOutlined,
  ExperimentOutlined,
  ToolOutlined,
  IdcardOutlined,
  SendOutlined,
  CarOutlined,
  FileTextOutlined,
  FunnelPlotOutlined,
  KeyOutlined,
  LinkOutlined,
  SafetyOutlined,
  ShopOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Dropdown, Flex, Layout, Menu, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@nks/shared';
import { useSession, useHasPermission } from '../../features/auth/session';
import { AlertsBell } from '../../features/notifications/AlertsBell';
import { ThemeToggle } from '../../components/ThemeToggle';
import { BranchSwitcher } from './BranchSwitcher';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export function AppShell(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSession((s) => s.user);
  const [navOpen, setNavOpen] = useState(false);
  const signOut = useSession((s) => s.signOut);
  const canSeeCases = useHasPermission(PERMISSIONS.CASE_VIEW);
  const canSeeFollowUps = useHasPermission(PERMISSIONS.FOLLOWUP_MANAGE);
  const canSeeRetention = useHasPermission(PERMISSIONS.RETENTION_VIEW);
  const canConfigureServiceDesk = useHasPermission(PERMISSIONS.SERVICE_DESK_CONFIGURE);

  // Customer service is one group; each entry shows only to people who may open it.
  const serviceItems = [
    ...(canSeeCases ? [{ key: '/cases', label: t('nav.cases') }] : []),
    ...(canSeeFollowUps ? [{ key: '/follow-ups', label: t('nav.followUps') }] : []),
    ...(canSeeCases ? [{ key: '/reminders', label: t('nav.reminders') }] : []),
    ...(canSeeRetention ? [{ key: '/retention', label: t('nav.retention') }] : []),
    ...(canConfigureServiceDesk ? [{ key: '/service-settings', label: t('nav.serviceDeskSettings') }] : []),
  ];

  // Leads first: it is the screen a sales advisor lives in.
  const items = [
    { key: '/leads', icon: <FunnelPlotOutlined />, label: t('nav.leads') },
    { key: '/quotations', icon: <FileTextOutlined />, label: t('nav.quotations') },
    { key: '/bookings', icon: <BookOutlined />, label: t('nav.bookings') },
    { key: '/financing', icon: <BankOutlined />, label: t('nav.financing') },
    { key: '/units', icon: <CarOutlined />, label: t('nav.units') },
    { key: '/model-map', icon: <LinkOutlined />, label: t('nav.modelMap') },
    { key: '/registration', icon: <IdcardOutlined />, label: t('nav.registration') },
    { key: '/deliveries', icon: <SendOutlined />, label: t('nav.deliveries') },
    { key: '/workshop', icon: <ToolOutlined />, label: t('nav.counter') },
    { key: '/workshop/board', icon: <BuildOutlined />, label: t('nav.workshop') },
    { key: '/workshop/bench', icon: <ExperimentOutlined />, label: t('nav.bench') },
    { key: '/warehouse', icon: <InboxOutlined />, label: t('nav.warehouse') },
    { key: '/parts/counts', icon: <ContainerOutlined />, label: t('nav.stockCounts') },
    { key: '/parts/counter', icon: <ShoppingCartOutlined />, label: t('nav.counterSale') },
    { key: '/pos', icon: <DollarOutlined />, label: t('nav.till') },
    { key: '/scan', icon: <BarcodeOutlined />, label: t('nav.scan') },
    { key: '/accounts', icon: <AuditOutlined />, label: t('nav.accounts') },
    { key: '/insights', icon: <BarChartOutlined />, label: t('nav.insights') },
    { key: '/customers', icon: <TeamOutlined />, label: t('nav.customers') },
    ...(serviceItems.length > 0
      ? [
          {
            key: 'customer-service',
            icon: <CustomerServiceOutlined />,
            label: t('nav.customerService'),
            children: serviceItems,
          },
        ]
      : []),
    { key: '/users', icon: <UserOutlined />, label: t('nav.users') },
    { key: '/roles', icon: <SafetyOutlined />, label: t('nav.roles') },
    { key: '/company', icon: <ShopOutlined />, label: t('nav.company') },
  ];
  // LONGEST match, not first. '/workshop' is a prefix of '/workshop/board', so
  // a first-match would leave the counter highlighted on every workshop screen.
  const selectable = [...items.filter((i) => !('children' in i)), ...serviceItems];
  const selected =
    selectable
      .sort((a, b) => b.key.length - a.key.length)
      .find((i) => location.pathname === i.key || location.pathname.startsWith(`${i.key}/`))?.key ?? '/leads';

  const nav = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selected]}
      defaultOpenKeys={serviceItems.some((i) => i.key === selected) ? ['customer-service'] : []}
      items={items}
      onClick={({ key }) => {
        setNavOpen(false);
        void navigate(key);
      }}
    />
  );

  return (
    <Layout style={{ minHeight: '100dvh' }}>
      <Sider breakpoint="lg" collapsedWidth={0} width={220} theme="dark" trigger={null}>
        <Flex
          align="center"
          className="nks-sider-brand"
          style={{ height: 56, padding: '0 16px', fontWeight: 600 }}
        >
          {t('app.name')}
        </Flex>
        {nav}
      </Sider>

      {/*
       * Nav on a phone is a drawer, not the Sider.
       *
       * The Sider's own zero-width trigger is a floating button pinned below the
       * header, and it landed directly on top of the first filter tab — tapping
       * "To issue" opened the nav instead. Expanding the Sider in place is no
       * better: 220px of a 390px screen leaves the content unusable.
       */}
      <Drawer
        placement="left"
        open={navOpen}
        onClose={() => {
          setNavOpen(false);
        }}
        width={260}
        closable={false}
        styles={{ body: { padding: 0, background: 'var(--nav-bg)' } }}
      >
        {nav}
      </Drawer>

      <Layout>
        <Header
          style={{
            // A token, not a literal (reference §1). AntD does not expose a
            // header border token, so the value comes from the CSS variable.
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Button
            type="text"
            aria-label={t('nav.openMenu')}
            className="nks-nav-trigger"
            icon={<MenuOutlined />}
            onClick={() => {
              setNavOpen(true);
            }}
          />
          <Flex align="center" justify="flex-end" gap={12} style={{ width: '100%', minWidth: 0 }}>
            {/*
             * Pinned. At 390px the three controls want 407px, and `flex-end`
             * makes the FIRST child absorb every pixel of the overflow — which
             * pushed the theme control 33px off the left edge and took the
             * "system" option off the screen entirely on a phone.
             */}
            <div style={{ flex: '0 0 auto' }}>
              <ThemeToggle />
            </div>
            {/* The branch name is the one thing here that can lose characters
             * and still be read, so it is what shrinks. */}
            <div style={{ flex: '1 1 180px', minWidth: 0, maxWidth: 280 }}>
              <BranchSwitcher />
            </div>
            <div style={{ flex: '0 0 auto' }}>
              <AlertsBell />
            </div>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'change-password',
                    icon: <KeyOutlined />,
                    label: t('nav.changePassword'),
                    onClick: () => { void navigate('/change-password'); },
                  },
                  { type: 'divider' },
                  {
                    key: 'signout',
                    icon: <LogoutOutlined />,
                    label: t('nav.signOut'),
                    onClick: () => void signOut(),
                  },
                ],
              }}
            >
              <Button type="text">
                <Text>{user?.name ?? ''}</Text>
              </Button>
            </Dropdown>
          </Flex>
        </Header>

        <Content style={{ padding: 16 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
